import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import colors from "assets/theme/base/colors"
import rgba from "assets/theme/functions/rgba"

// The 5-step approval-rule workflow. `caption` is the one-line explanation
// shown beneath each step title so the whole flow reads at a glance.
export const EDITOR_STEPS = [
  { key: "details", label: "Details", caption: "Define scope" },
  { key: "conditions", label: "Conditions", caption: "Set criteria" },
  { key: "steps", label: "Approval Steps", caption: "Define approval flow" },
  { key: "doa", label: "Approval Limits", caption: "Set thresholds" },
  { key: "preview", label: "Preview & Publish", caption: "Review & publish" },
]

export const getStepCompletion = (rule) => {
  const ruleData = rule?.rule || rule
  const steps = rule?.steps || []
  return {
    details: Boolean(ruleData?.name?.trim()),
    conditions: true,
    steps: steps.length > 0,
    doa: true,
    preview: Boolean(ruleData?.name?.trim()) && steps.length > 0,
  }
}

const CIRCLE = 34
const LINE = 2

const circleStyle = (state) => ({
  width: CIRCLE,
  height: CIRCLE,
  minWidth: CIRCLE,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.15s ease",
  ...(state === "active" && {
    backgroundColor: colors.info.main,
    color: "#fff",
    boxShadow: `0 0 0 4px ${rgba(colors.info.main, 0.18)}`,
  }),
  ...(state === "complete" && {
    backgroundColor: colors.info.main,
    color: "#fff",
  }),
  ...(state === "future" && {
    backgroundColor: "#fff",
    color: colors.text.main,
    border: `1.5px solid ${colors.grey[300]}`,
  }),
})

const Connector = ({ done, hidden }) => (
  <MDBox
    sx={{
      flex: 1,
      height: LINE,
      borderRadius: LINE,
      backgroundColor: done ? colors.info.main : colors.grey[300],
      visibility: hidden ? "hidden" : "visible",
    }}
  />
)

// One column of the desktop stepper: connector — circle — connector on top,
// wrapping label underneath. Equal-width columns keep the row inside the card
// at any viewport, so the row never needs to scroll sideways.
const StepColumn = ({ step, index, state, isFirst, isLast, activeIdx, onSelect }) => {
  const isActive = state === "active"

  return (
    <MDBox
      role="button"
      tabIndex={0}
      aria-current={isActive ? "step" : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect()
        }
      }}
      px={0.5}
      pt={0.5}
      pb={1}
      borderRadius="lg"
      sx={{
        cursor: "pointer",
        minWidth: 0,
        outline: "none",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: rgba(colors.info.main, 0.06) },
        "&:focus-visible": { backgroundColor: rgba(colors.info.main, 0.1) },
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1}>
        <Connector done={index <= activeIdx} hidden={isFirst} />
        <MDBox sx={circleStyle(state)}>
          {state === "complete" ? (
            <Icon sx={{ fontSize: 20 }}>check</Icon>
          ) : (
            <MDTypography
              variant="button"
              fontWeight="bold"
              color={isActive ? "white" : "text"}
              sx={{ lineHeight: 1 }}
            >
              {index + 1}
            </MDTypography>
          )}
        </MDBox>
        <Connector done={index < activeIdx} hidden={isLast} />
      </MDBox>

      <MDBox mt={1} textAlign="center">
        <MDTypography
          variant="button"
          fontWeight={isActive ? "bold" : "medium"}
          color={state === "future" ? "text" : "dark"}
          sx={{
            display: "block",
            lineHeight: 1.25,
            opacity: state === "future" ? 0.85 : 1,
          }}
        >
          {step.label}
        </MDTypography>
        <MDTypography
          variant="caption"
          color="text"
          sx={{
            display: { xs: "none", xl: "block" },
            lineHeight: 1.25,
            opacity: 0.8,
          }}
        >
          {step.caption}
        </MDTypography>
      </MDBox>
    </MDBox>
  )
}

const RuleEditorStepper = ({ activeStep, onStepChange, rule }) => {
  const completion = getStepCompletion(rule)
  const activeIdx = Math.max(
    0,
    EDITOR_STEPS.findIndex((s) => s.key === activeStep)
  )
  const active = EDITOR_STEPS[activeIdx]

  const stateFor = (idx) => {
    if (idx === activeIdx) return "active"
    // A step counts as complete when it sits before the active step and its
    // own requirement is met — otherwise it's still an open (future) step.
    if (idx < activeIdx && completion[EDITOR_STEPS[idx].key]) return "complete"
    return "future"
  }

  return (
    <MDBox>
      {/* Compact progress on phones — the full row needs more width than there is */}
      <MDBox sx={{ display: { xs: "block", md: "none" } }}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          gap={1}
          mb={1}
        >
          <MDTypography variant="button" fontWeight="bold" color="dark">
            {active?.label}
          </MDTypography>
          <MDTypography variant="caption" color="text" sx={{ flexShrink: 0 }}>
            Step {activeIdx + 1} of {EDITOR_STEPS.length}
          </MDTypography>
        </MDBox>
        <MDTypography variant="caption" color="text" display="block" mb={1.25}>
          {active?.caption}
        </MDTypography>
        <MDBox display="flex" gap={0.75}>
          {EDITOR_STEPS.map((step, idx) => (
            <MDBox
              key={step.key}
              role="button"
              tabIndex={0}
              aria-label={`Go to step ${idx + 1}: ${step.label}`}
              aria-current={idx === activeIdx ? "step" : undefined}
              onClick={() => onStepChange(step.key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onStepChange(step.key)
                }
              }}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                cursor: "pointer",
                backgroundColor:
                  idx <= activeIdx ? colors.info.main : colors.grey[300],
                opacity: idx === activeIdx || idx > activeIdx ? 1 : 0.55,
              }}
            />
          ))}
        </MDBox>
      </MDBox>

      {/* Full row from tablet up */}
      <MDBox
        sx={{
          display: { xs: "none", md: "grid" },
          gridTemplateColumns: `repeat(${EDITOR_STEPS.length}, minmax(0, 1fr))`,
        }}
      >
        {EDITOR_STEPS.map((step, idx) => (
          <StepColumn
            key={step.key}
            step={step}
            index={idx}
            state={stateFor(idx)}
            isFirst={idx === 0}
            isLast={idx === EDITOR_STEPS.length - 1}
            activeIdx={activeIdx}
            onSelect={() => onStepChange(step.key)}
          />
        ))}
      </MDBox>
    </MDBox>
  )
}

export default RuleEditorStepper
